import { useAuth0 } from '@auth0/auth0-react'

import { AppDispatch } from '@redux/store'

import useCampaignSelector from '@hooks/useCampaignSelector'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import {
  isAdmin, isAgencyAdmin, isAgencyUser, isAgent, isBrandUser, isMasterAdmin, isProviderDirector, isProviderUser,
} from '@functions/user'

import type { ModuleState } from '@redux/modules/module'
import * as userActions from '@redux/modules/user'

import type {
  AgencyModel, BrandModel, ProviderModel, RoleModel, UserModel,
} from '@models/types'
import type { RoleTitle } from '@functions/role'

import api from '@functions/api'

export const defaultRequestOptions = {
  include_agency_brand: true,
  include_user_intercom_jwt: true,
  include_provider_is_publication_provider: true,
  include_user_association: true,
  include_user_frontend_permissions: true,
  include_user_impersonated_user: true,
  include_user_role_title: true,
  include_user_type: true,
}

const findAudience = (roleTitle: RoleTitle) => {
  if (isAgencyUser(roleTitle)) return 'agency'
  if (isBrandUser(roleTitle)) return 'brand'
  if (isProviderUser(roleTitle)) return 'provider'
  if (isAdmin(roleTitle)) return 'admin'

  return ''
}

const signOut = () => api('/users/sign_out_user', {}, { method: 'POST' })

type UseRelationsPayload = {
  agency: Partial<AgencyModel>,
  brand: Partial<BrandModel>,
  provider: Partial<ProviderModel>,
  role: Partial<RoleModel>,
}

export function useRelations(user: Partial<UserModel>) : UseRelationsPayload {
  const { association_id, association_type, role_id } = user

  const {
    agencies, brands, providers, roles,
  } = useSelector(reduxState => reduxState.entities)

  const agency: Partial<AgencyModel> = association_type === 'Agency' && agencies[association_id!]
    ? agencies[association_id!]
    : {}

  const brand: Partial<BrandModel> = association_type === 'Brand' && brands[association_id!]
    ? brands[association_id!]
    : {}
  const provider: Partial<ProviderModel> = association_type === 'Provider' && providers[association_id!]
    ? providers[association_id!]
    : {}

  const role: Partial<RoleModel> = role_id && roles[role_id] ? roles[role_id] : {}

  return {
    agency,
    brand,
    provider,
    role,
  }
}

type Presign = {
  application_key: string,
  hash?: string,
  path: string,
}

type PresignUrlParams = {
  preSignParams: Presign,
  dispatch: AppDispatch,
  user: UserModel,
}

const loginToExternalPlatform = (params: PresignUrlParams) => {
  const {
    dispatch, user, preSignParams,
  } = params

  const { application_key: applicationKey, hash, path } = preSignParams

  const { presignUrl: presignUrlFn } = userActions
  return dispatch(presignUrlFn(user, { applicationKey, hash, path })).then((response) => {
    if (response.success){
      const { data } = response
      const { url } = data as { url?: string }
      if (url) window.open(url)
    }
  })
}

interface UseCurrentUserOptions {
  performHttpRequests?: boolean,
}

function useCurrentUser(options?: UseCurrentUserOptions) {
  const { performHttpRequests } = options || {}

  const dispatch = useDispatch()
  const auth0Payload = useAuth0()
  const { logout, user } = auth0Payload

  const result = useReduxAction(
    'currentUser',
    'loadCurrentUser',
    defaultRequestOptions,
    [performHttpRequests],
    {
      // @ts-expect-error
      shouldPerformFn: (entityReducer: ModuleState) => {
        const { errors, loaded, loading } = entityReducer

        return performHttpRequests && !loaded && !loading && !errors?.length
      },
    },
  )

  if (result.errors?.includes('Not Authenticated')){
    logout()
  }

  const { 'https://www.realhubapp.com/role': authenticatedRoleTitle } = user || {}
  // @ts-expect-error
  const entityReducer: ModuleState & { result: Partial<UserModel> } = useSelector(reduxState => reduxState.currentUser)
  const { loaded, loading, result: currentUserResult = {} } = entityReducer

  const originalUserRoleTitle = currentUserResult?.role_title

  const impersonatedUser: Partial<UserModel> = currentUserResult?.impersonated_user || {}
  const isImpersonatingUser = !!impersonatedUser.id

  const currentUser:Partial<UserModel> = isImpersonatingUser ? impersonatedUser : currentUserResult

  const isEbuAdmin = currentUser.actable?.ebu_admin || false

  const {
    agency, brand, provider, role,
  } = useRelations(currentUser)

  const roleTitle: RoleTitle = role.title || authenticatedRoleTitle
  const impersonatedRoleTitle = isImpersonatingUser ? role.title : null

  const canAccessInactiveAgency = Boolean(
    originalUserRoleTitle
  && (isAdmin(originalUserRoleTitle) || isBrandUser(originalUserRoleTitle))
  && agency?.id
  && !agency.active,
  )
  // When we have a selected campaign, we want to use the user permissions built off
  // the campaign's agency - not the user's agency (they can be different in brand groups)
  const { agencyPermissions } = useCampaignSelector()

  return {
    agency: agency as AgencyModel,
    audience: findAudience(roleTitle),
    authenticatedRoleTitle,
    brand: brand as BrandModel,
    callbacks: {
      loginToExternalPlatform: (preSignParams: Presign) => loginToExternalPlatform({
        dispatch,
        preSignParams,
        // @ts-expect-error
        user: currentUser,
      }),
      logout: () => {
        signOut()
        logout({ returnTo: window.location.origin })
      },
      signOut: () => signOut(),
    },
    canAccessInactiveAgency,
    currentUser,
    impersonatedRoleTitle,
    impersonatedUser,
    impersonatingUser: currentUserResult,
    isAdmin: isAdmin(roleTitle),
    isAgencyAdmin: isAgencyAdmin(roleTitle),
    isAgencyUser: isAgencyUser(roleTitle),
    isAgent: isAgent(roleTitle),
    isBrandUser: isBrandUser(roleTitle),
    isEbuAdmin,
    isImpersonatingUser,
    isMasterAdmin: isMasterAdmin(roleTitle),
    isProviderDirector: isProviderDirector(roleTitle),
    isProviderUser: isProviderUser(roleTitle),
    loaded,
    loading,
    permissions: {
      admin: currentUser.frontend_permissions?.admin,
      agency: agencyPermissions || currentUser.frontend_permissions?.agency,
      brand: currentUser.frontend_permissions?.brand,
      default: currentUser.frontend_permissions?.default || true,
      enableCustomPermissions: currentUser.enable_custom_permissions || false,
    },
    provider,
    roleTitle,
  }
}

export type CurrentUserPayload = ReturnType<typeof useCurrentUser>

export default useCurrentUser
